// API gateway (plan 5.11, 11.11.5; P4): `/gateway/anthropic/*` and `/gateway/openai/*`. The gateway token (axh_gw_…,
// or a service-account token axh_svc_…) goes where the provider SDK puts its key. Only allow-listed paths relay;
// recorded paths also write gateway_calls. Bodies pass through unchanged; errors use the provider's own format.
import { z } from 'zod';
import { TOKEN_PREFIX } from '../enums.js';

export const GATEWAY_PREFIX = { anthropic: '/gateway/anthropic', openai: '/gateway/openai' } as const;

/** Allow-list (plan 5.11): `record` = relayed and recorded; `relay` = relayed only. Anything else → 404 provider error. */
export const GATEWAY_ROUTES = [
  { provider: 'anthropic', method: 'POST', path: 'v1/messages', mode: 'record' },
  { provider: 'anthropic', method: 'POST', path: 'v1/messages/count_tokens', mode: 'relay' },
  { provider: 'anthropic', method: 'GET', path: 'v1/models', mode: 'relay' },
  { provider: 'anthropic', method: 'GET', path: 'api/hello', mode: 'relay' },
  { provider: 'openai', method: 'POST', path: 'v1/chat/completions', mode: 'record' },
  { provider: 'openai', method: 'POST', path: 'v1/responses', mode: 'record' },
  { provider: 'openai', method: 'GET', path: 'v1/models', mode: 'relay' },
] as const;
export type GatewayRoute = (typeof GATEWAY_ROUTES)[number];

const GatewayToken = z.string().refine((t) => t.startsWith(TOKEN_PREFIX.gateway) || t.startsWith(TOKEN_PREFIX.service), 'gateway or service token');
/**
 * Anthropic SDKs send `x-api-key`; OpenAI SDKs send `Authorization: Bearer`. The Anthropic route also takes a Bearer
 * token (Claude Code's ANTHROPIC_AUTH_TOKEN, used by `prs claude`). Our token never reaches the provider.
 */
export const AnthropicGatewayHeaders = z.object({ 'x-api-key': GatewayToken });
export const OpenAIGatewayHeaders = z.object({ authorization: z.string().regex(/^Bearer\s+\S+$/) });

/** Provider request/response bodies pass through unchanged (not modelled here). */
export const ProviderPassthrough = z.unknown();

export const AnthropicErrorBody = z.object({
  type: z.literal('error'),
  error: z.object({
    type: z.enum(['invalid_request_error', 'authentication_error', 'permission_error', 'not_found_error', 'rate_limit_error', 'api_error', 'overloaded_error']),
    message: z.string(),
  }),
});
export const OpenAIErrorBody = z.object({ error: z.object({ message: z.string(), type: z.string(), code: z.string().nullable(), param: z.string().nullable() }) });

/**
 * Gateway refusals → provider format: token missing/unknown/expired/revoked 401 · consent off (default block) 403 ·
 * model not allowed 400 · path not allowed 404 · per-token/day limits and per-person model limits 429 · upstream connect fail 502 · timeout 504.
 */
export const GATEWAY_REFUSALS = ['token_invalid', 'consent_blocked', 'model_not_allowed', 'model_quota_exceeded', 'route_not_allowed', 'rate_limited', 'upstream_unreachable', 'upstream_timeout'] as const;

/** Health probes (plan 4.6; P0). */
export const HealthResponse = z.object({ status: z.literal('ok') });
export const ReadyResponse = z.object({
  status: z.enum(['ready', 'not_ready']),
  checks: z.object({ db: z.enum(['ok', 'fail']), migrations: z.enum(['ok', 'fail']), keys: z.enum(['ok', 'fail']), queue: z.enum(['ok', 'fail']) }),
  version: z.string(),
});

/**
 * `/downloads/*` — client release files. The release carries the version in each name (prs-setup-0.1.3.exe); these plain
 * names stay valid as aliases of the current version, so existing links and install scripts keep working.
 */
export const CLIENT_DOWNLOAD_FILES = ['prs.exe', 'prs-setup.exe', 'prs-cli.tgz', 'metis-hub.vsix'] as const;
export type ClientDownloadFile = (typeof CLIENT_DOWNLOAD_FILES)[number];
/** prs-setup-0.1.3.exe: one of the client files with an x.y.z version before the extension. */
export const VERSIONED_CLIENT_FILE = /^(prs|prs-setup|prs-cli|metis-hub)-(\d{1,4}\.\d{1,4}\.\d{1,6})\.(exe|tgz|vsix)$/;
/** prs-setup.exe + 0.1.3 → prs-setup-0.1.3.exe */
export function versionedClientFile(alias: ClientDownloadFile, version: string): string {
  const name = alias.replace(/\.(exe|tgz|vsix)$/, `-${version}.$1`);
  if (!VERSIONED_CLIENT_FILE.test(name)) throw new Error(`invalid client version ${version}`);
  return name;
}
/** prs-setup-0.1.3.exe → prs-setup.exe; null for any other name (prs-cli-0.1.3.exe is not a client file). */
export function clientFileAlias(name: string): ClientDownloadFile | null {
  const m = VERSIONED_CLIENT_FILE.exec(name); if (!m) return null;
  const alias = `${m[1]}.${m[3]}`;
  return (CLIENT_DOWNLOAD_FILES as readonly string[]).includes(alias) ? (alias as ClientDownloadFile) : null;
}
/** Developer tool installers (Node.js · Git · GitLab CLI · Codex · Claude Code · METIS CLI), from packages/cli-config/setup. */
export const SETUP_SCRIPT_FILES = { windows: 'setup-tools.ps1', mac: 'setup-tools-mac.sh', linux: 'setup-tools-linux.sh' } as const;
export const DOWNLOAD_FILES = ['connect.sh', 'connect.ps1', ...CLIENT_DOWNLOAD_FILES, 'client-release.json', 'SHA256SUMS.txt', SETUP_SCRIPT_FILES.windows, SETUP_SCRIPT_FILES.mac, SETUP_SCRIPT_FILES.linux] as const;
/** client-release.json. serverUrl: default server built into the files (scripts/build-client-release.mjs); older builds omit it. */
export const ClientRelease = z.object({ version: z.string().max(40), builtAt: z.string().max(40), platform: z.literal('win32-x64'), node: z.string().max(30), vscode: z.string().max(30), signed: z.boolean(), serverUrl: z.url({ protocol: /^https?$/ }).max(300).optional(), files: z.array(z.object({
  // 0.1.3 and earlier listed the plain names; later releases list versioned names with their plain alias.
  name: z.union([z.enum(CLIENT_DOWNLOAD_FILES), z.string().regex(VERSIONED_CLIENT_FILE)]), alias: z.enum(CLIENT_DOWNLOAD_FILES).optional(),
  bytes: z.number().int().positive(), sha256: z.string().regex(/^[a-f0-9]{64}$/) }).refine((f) => !f.alias || clientFileAlias(f.name) === f.alias, 'alias does not match the file name')).max(4) });
export const DownloadParams = z.object({ file: z.union([z.enum(DOWNLOAD_FILES), z.string().regex(VERSIONED_CLIENT_FILE)]) });
export const DownloadFile = z.unknown();

export const gatewaySchemas = {
  AnthropicGatewayHeaders,
  OpenAIGatewayHeaders,
  ProviderPassthrough,
  AnthropicErrorBody,
  OpenAIErrorBody,
  HealthResponse,
  ReadyResponse,
  DownloadParams,
  DownloadFile,
} as const;
